import {useCallback, useEffect, useState, useMemo} from "react";
import {Card, Table, Statistic, Row, Col, Tag, Spin, Button, Space, Typography, Modal} from "antd";
import {
    TrophyOutlined,
    GiftOutlined,
    ReloadOutlined,
    ManOutlined,
    WomanOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import {useLotteryApi} from "../../Components/lottery/api/lotteryApi";

const {Title, Text} = Typography;

const AdminLotteryPanel = () => {
    const api = useLotteryApi();
    const [loading, setLoading] = useState(true);
    const [allLotteries, setAllLotteries] = useState([]);
    const [winnableLotteries, setWinnableLotteries] = useState({M: [], F: []});
    const [winners, setWinners] = useState([]);

    const refreshWinners = useCallback(async () => {
        const {ok, data} = await api.fetchWinners();
        if (ok) setWinners(data);
    }, [api]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const [all, winnable, winnerList] = await Promise.all([
            api.fetchAllLotteries(),
            api.fetchWinnableLotteries(),
            api.fetchWinners(),
        ]);
        if (all.ok) setAllLotteries(all.data);
        if (winnable.ok) setWinnableLotteries(winnable.data);
        if (winnerList.ok) setWinners(winnerList.data);
        setLoading(false);
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRefresh = useCallback(async () => {
        setLoading(true);
        await refreshWinners();
        setLoading(false);
    }, [refreshWinners]);

    const handleDeleteWinner = useCallback((custno) => {
        Modal.confirm({
            title: 'Ялагчийг устгах',
            content: 'Та энэ ялагчийг устгахдаа итгэлтэй байна уу?',
            okText: 'Тийм',
            okType: 'danger',
            cancelText: 'Үгүй',
            onOk: async () => {
                if (await api.deleteWinner(custno)) await refreshWinners();
            }
        });
    }, [api, refreshWinners]);

    const clearWinners = useCallback(() => {
        Modal.confirm({
            title: 'Бүх ялагчдыг устгах',
            content: 'Та бүх ялагчдыг устгахдаа итгэлтэй байна уу? Энэ үйлдлийг буцаах боломжгүй.',
            okText: 'Тийм',
            okType: 'danger',
            cancelText: 'Үгүй',
            onOk: async () => {
                if (await api.clearWinners()) await refreshWinners();
            }
        });
    }, [api, refreshWinners]);

    // Group winnable lotteries by custno
    const groupedMaleLotteries = useMemo(() => {
        const grouped = {};
        (winnableLotteries.M || []).forEach(item => {
            if (!grouped[item.custno]) {
                grouped[item.custno] = {
                    custno: item.custno,
                    name: item.name,
                    regnum: item.regnum,
                    gender: item.gender,
                    lotteryCount: 0,
                    lotteries: []
                };
            }
            grouped[item.custno].lotteryCount++;
            grouped[item.custno].lotteries.push(item.lottery);
        });
        return Object.values(grouped);
    }, [winnableLotteries.M]);

    const groupedFemaleLotteries = useMemo(() => {
        const grouped = {};
        (winnableLotteries.F || []).forEach(item => {
            if (!grouped[item.custno]) {
                grouped[item.custno] = {
                    custno: item.custno,
                    name: item.name,
                    regnum: item.regnum,
                    gender: item.gender,
                    lotteryCount: 0,
                    lotteries: []
                };
            }
            grouped[item.custno].lotteryCount++;
            grouped[item.custno].lotteries.push(item.lottery);
        });
        return Object.values(grouped);
    }, [winnableLotteries.F]);

    const winnersColumns = [
        {
            title: '№',
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'СИФ',
            dataIndex: 'custno',
            key: 'custno',
        },
        {
            title: 'Нэр',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Регистр',
            dataIndex: 'regnum',
            key: 'regnum',
        },
        {
            title: 'Урамшууллын эрх',
            dataIndex: 'lottery',
            key: 'lottery',
        },
        {
            title: 'Хүйс',
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            render: (gender) => (
                <Tag color={gender === 'M' ? 'blue' : 'pink'} icon={gender === 'M' ? <ManOutlined/> : <WomanOutlined/>}>
                    {gender === 'M' ? 'Эрэгтэй' : 'Эмэгтэй'}
                </Tag>
            ),
        },
        {
            title: 'Үйлдэл',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<DeleteOutlined/>}
                    onClick={() => handleDeleteWinner(record.custno)}
                >
                    Устгах
                </Button>
            ),
        },
    ];

    const winnableLotteriesColumns = [
        {
            title: '№',
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'СИФ',
            dataIndex: 'custno',
            key: 'custno',
        },
        {
            title: 'Нэр',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Регистр',
            dataIndex: 'regnum',
            key: 'regnum',
        },
        {
            title: 'Урамшууллын эрхийн тоо',
            dataIndex: 'lotteryCount',
            key: 'lotteryCount',
            sorter: (a, b) => a.lotteryCount - b.lotteryCount,
            render: (count) => (
                <Tag color="blue">{count}</Tag>
            ),
        },
        {
            title: 'Хүйс',
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            render: (gender) => (
                <Tag color={gender === 'M' ? 'blue' : 'pink'}>
                    {gender === 'M' ? 'Эрэгтэй' : 'Эмэгтэй'}
                </Tag>
            ),
        },
    ];

    const maleWinners = winners.filter(w => w.gender === 'M').length;
    const femaleWinners = winners.filter(w => w.gender === 'F').length;
    const totalWinnableMale = groupedMaleLotteries.length;
    const totalWinnableFemale = groupedFemaleLotteries.length;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
                <Spin size="large" tip="Өгөгдөл ачааллаж байна..."/>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <Title level={2} className="m-0">
                        <TrophyOutlined className="mr-2"/>
                        Урамшууллын удирдлагын самбар
                    </Title>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        loading={loading}
                    >
                        Шинэчлэх
                    </Button>
                </div>

                <Row gutter={[16, 16]} className="mb-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Нийт урамшууллын эрх"
                                value={allLotteries.length}
                                prefix={<GiftOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Нийт ялагч"
                                value={winners.length}
                                prefix={<TrophyOutlined />}
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Эрэгтэй ялагч"
                                value={`${maleWinners} / ${totalWinnableMale}`}
                                prefix={<ManOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card>
                            <Statistic
                                title="Эмэгтэй ялагч"
                                value={`${femaleWinners} / ${totalWinnableFemale}`}
                                prefix={<WomanOutlined />}
                                valueStyle={{ color: '#eb2f96' }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card
                    title={
                        <Space>
                            <TrophyOutlined style={{ color: '#faad14' }} />
                            <span>Урамшууллын эзэд</span>
                            <Tag color="gold">{winners.length}</Tag>
                        </Space>
                    }
                    className="mb-6"
                >
                    <Table
                        columns={winnersColumns}
                        dataSource={winners}
                        rowKey="lottery"
                        pagination={false}
                        scroll={{ x: 800 }}
                        footer={() => (
                            <Space>
                                <Button
                                    type="primary"
                                    danger
                                    onClick={clearWinners}
                                    disabled={winners.length === 0}
                                >
                                    Бүх ялагчдыг устгах
                                </Button>
                            </Space>
                        )}
                    />
                </Card>

                <Card
                    title={
                        <Space>
                            <ManOutlined style={{ color: '#1890ff' }} />
                            <span>Эрэгтэй оролцогчид</span>
                            <Tag color="blue">{totalWinnableMale}</Tag>
                        </Space>
                    }
                    className="mb-6"
                >
                    <Table
                        columns={winnableLotteriesColumns}
                        dataSource={groupedMaleLotteries}
                        rowKey="custno"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 800 }}
                        expandable={{
                            expandedRowRender: (record) => (
                                <div className="p-2">
                                    <Text strong>Урамшууллын эрхүүд: </Text>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {record.lotteries.map((lottery, idx) => (
                                            <Tag key={idx} color="blue">{lottery}</Tag>
                                        ))}
                                    </div>
                                </div>
                            ),
                        }}
                    />
                </Card>

                <Card
                    title={
                        <Space>
                            <WomanOutlined style={{ color: '#eb2f96' }} />
                            <span>Эмэгтэй оролцогчид</span>
                            <Tag color="pink">{totalWinnableFemale}</Tag>
                        </Space>
                    }
                >
                    <Table
                        columns={winnableLotteriesColumns}
                        dataSource={groupedFemaleLotteries}
                        rowKey="custno"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 800 }}
                        expandable={{
                            expandedRowRender: (record) => (
                                <div className="p-2">
                                    <Text strong>Урамшууллын эрхүүд: </Text>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {record.lotteries.map((lottery, idx) => (
                                            <Tag key={idx} color="pink">{lottery}</Tag>
                                        ))}
                                    </div>
                                </div>
                            ),
                        }}
                    />
                </Card>
            </div>
        </div>
    );
};

export default AdminLotteryPanel;